/**
 * Universal Filters Component - ENHANCED
 * Reusable filter component for Countries, Retailers, Brands, Stores, Categories, etc.
 * Used across Executive Dashboard, Stockout Management, and Inventory List
 *
 * New Features:
 * - Date range filtering
 * - Export functionality (Excel/PDF)
 * - Saved filter presets
 * - Time period filters (week/month/quarter/year)
 */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Menu,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  FileDownload as ExportIcon,
  Save as SaveIcon,
  BookmarkBorder as PresetIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { FilterOptions } from '../../types/executive';

export interface UniversalFiltersProps {
  // Filter values
  countries?: string[];
  cities?: string[];
  retailers?: string[];
  brands?: string[];
  stores?: string[];
  categories?: string[];
  subcategories?: string[];
  abcClassifications?: string[];
  lifecycleStages?: string[];
  dateRange?: { start: string; end: string };
  timePeriod?: 'week' | 'month' | 'quarter' | 'year' | 'custom';

  // onChange handlers
  onCountriesChange?: (countries: string[]) => void;
  onCitiesChange?: (cities: string[]) => void;
  onRetailersChange?: (retailers: string[]) => void;
  onBrandsChange?: (brands: string[]) => void;
  onStoresChange?: (stores: string[]) => void;
  onCategoriesChange?: (categories: string[]) => void;
  onSubcategoriesChange?: (subcategories: string[]) => void;
  onAbcClassificationsChange?: (abcClassifications: string[]) => void;
  onLifecycleStagesChange?: (lifecycleStages: string[]) => void;
  onDateRangeChange?: (dateRange: { start: string; end: string }) => void;
  onTimePeriodChange?: (period: 'week' | 'month' | 'quarter' | 'year' | 'custom') => void;

  // Clear all handler
  onClearAll?: () => void;

  // Export handlers
  onExportExcel?: () => void;
  onExportPDF?: () => void;

  // Apply filters handler (for lazy loading)
  onApplyFilters?: () => void;

  // Configuration
  showCountries?: boolean;
  showCities?: boolean;
  showRetailers?: boolean;
  showBrands?: boolean;
  showStores?: boolean;
  showCategories?: boolean;
  showSubcategories?: boolean;
  showAbcClassifications?: boolean;
  showLifecycleStages?: boolean;
  showDateRange?: boolean;
  showTimePeriod?: boolean;
  showExport?: boolean;
  showPresets?: boolean;
  showApplyButton?: boolean;

  // Additional custom filters (rendered after standard filters)
  customFilters?: React.ReactNode;

  // Styling
  elevation?: number;
  sx?: any;

  // Loading state
  isApplying?: boolean;
}

const ABC_LABELS: Record<string, string> = {
  'a': 'A - Fast Mover',
  'b': 'B - Medium Mover',
  'c': 'C - Slow Mover',
  'd': 'D - Very Slow Mover',
  'e': 'E - Dead Stock',
};

const UniversalFilters: React.FC<UniversalFiltersProps> = ({
  countries = [],
  cities = [],
  retailers = [],
  brands = [],
  stores = [],
  categories = [],
  subcategories = [],
  abcClassifications = [],
  lifecycleStages = [],
  dateRange,
  timePeriod = 'month',
  onCountriesChange,
  onCitiesChange,
  onRetailersChange,
  onBrandsChange,
  onStoresChange,
  onCategoriesChange,
  onSubcategoriesChange,
  onAbcClassificationsChange,
  onLifecycleStagesChange,
  onDateRangeChange,
  onTimePeriodChange,
  onClearAll,
  onExportExcel,
  onExportPDF,
  onApplyFilters,
  showCountries = false,
  showCities = false,
  showRetailers = false,
  showBrands = true,
  showStores = true,
  showCategories = false,
  showSubcategories = false,
  showAbcClassifications = false,
  showLifecycleStages = false,
  showDateRange = false,
  showTimePeriod = false,
  showExport = false,
  showPresets = false,
  showApplyButton = false,
  customFilters,
  elevation = 1,
  sx,
  isApplying = false,
}) => {
  // Export menu state
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
  const [presetAnchorEl, setPresetAnchorEl] = useState<null | HTMLElement>(null);
  // Fetch filter options (use unified dashboard endpoint by default, fallback to executive)
  const { data: filterOptions, isLoading } = useQuery<FilterOptions>({
    queryKey: ['unifiedDashboardFilterOptions'],
    queryFn: async () => {
      try {
        // Try unified dashboard endpoint first
        const response = await api.getUnifiedDashboardFilters();
        return response.data.data || response.data;
      } catch (error) {
        // Fallback to executive dashboard endpoint
        const response = await api.getExecutiveFilterOptions();
        return response.data;
      }
    },
  });

  // Calculate active filter count
  const activeFilterCount = [
    ...countries,
    ...cities,
    ...retailers,
    ...brands,
    ...stores,
    ...categories,
    ...subcategories,
    ...abcClassifications,
    ...lifecycleStages,
  ].length;

  const handleClearAll = () => {
    if (onCountriesChange) onCountriesChange([]);
    if (onCitiesChange) onCitiesChange([]);
    if (onRetailersChange) onRetailersChange([]);
    if (onBrandsChange) onBrandsChange([]);
    if (onStoresChange) onStoresChange([]);
    if (onCategoriesChange) onCategoriesChange([]);
    if (onSubcategoriesChange) onSubcategoriesChange([]);
    if (onAbcClassificationsChange) onAbcClassificationsChange([]);
    if (onLifecycleStagesChange) onLifecycleStagesChange([]);
    if (onClearAll) onClearAll();
  };

  const handleExport = (type: 'excel' | 'pdf') => {
    if (type === 'excel' && onExportExcel) {
      onExportExcel();
    } else if (type === 'pdf' && onExportPDF) {
      onExportPDF();
    }
    setExportAnchorEl(null);
  };

  const handleSavePreset = () => {
    // Save current filter state to localStorage
    const presetName = prompt('Enter a name for this filter preset:');
    if (presetName) {
      const preset = {
        name: presetName,
        countries,
        retailers,
        brands,
        stores,
        categories,
        subcategories,
        abcClassifications,
        lifecycleStages,
        dateRange,
        timePeriod,
      };
      const presets = JSON.parse(localStorage.getItem('filterPresets') || '[]');
      presets.push(preset);
      localStorage.setItem('filterPresets', JSON.stringify(presets));
      setPresetAnchorEl(null);
    }
  };

  const handleLoadPreset = (presetIndex: number) => {
    const presets = JSON.parse(localStorage.getItem('filterPresets') || '[]');
    const preset = presets[presetIndex];
    if (preset) {
      if (onCountriesChange) onCountriesChange(preset.countries || []);
      if (onRetailersChange) onRetailersChange(preset.retailers || []);
      if (onBrandsChange) onBrandsChange(preset.brands || []);
      if (onStoresChange) onStoresChange(preset.stores || []);
      if (onCategoriesChange) onCategoriesChange(preset.categories || []);
      if (onSubcategoriesChange) onSubcategoriesChange(preset.subcategories || []);
      if (onAbcClassificationsChange) onAbcClassificationsChange(preset.abcClassifications || []);
      if (onLifecycleStagesChange) onLifecycleStagesChange(preset.lifecycleStages || []);
      if (onDateRangeChange && preset.dateRange) onDateRangeChange(preset.dateRange);
      if (onTimePeriodChange && preset.timePeriod) onTimePeriodChange(preset.timePeriod);
    }
    setPresetAnchorEl(null);
  };

  const savedPresets = JSON.parse(localStorage.getItem('filterPresets') || '[]');

  // Count visible filters
  const visibleFiltersCount = [
    showCountries,
    showCities,
    showRetailers,
    showBrands,
    showStores,
    showCategories,
    showSubcategories,
    showAbcClassifications,
    showLifecycleStages,
  ].filter(Boolean).length;

  // Calculate grid size based on number of visible filters
  const getGridSize = () => {
    if (visibleFiltersCount <= 4) return 3; // 4 columns
    if (visibleFiltersCount <= 6) return 2; // 6 columns
    return 2; // Default to 2 for many filters
  };

  const gridSize = getGridSize();

  if (isLoading) {
    return (
      <Paper elevation={elevation} sx={{ p: 2, mb: 3, ...sx }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={elevation} sx={{ p: 2, mb: 3, ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterIcon color="primary" />
          <Typography variant="h6">Filters</Typography>
          <Chip
            label={`${activeFilterCount} active`}
            size="small"
            color={activeFilterCount > 0 ? 'primary' : 'default'}
          />
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Export Button */}
          {showExport && (
            <Tooltip title="Export Data">
              <IconButton
                size="small"
                onClick={(e) => setExportAnchorEl(e.currentTarget)}
              >
                <ExportIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Presets Button */}
          {showPresets && (
            <Tooltip title="Filter Presets">
              <IconButton
                size="small"
                onClick={(e) => setPresetAnchorEl(e.currentTarget)}
              >
                <PresetIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Refresh Button */}
          <Tooltip title="Refresh Data">
            <IconButton
              size="small"
              onClick={() => onApplyFilters && onApplyFilters()}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {/* Time Period Filter */}
        {showTimePeriod && onTimePeriodChange && (
          <Grid item xs={12} sm={6} md={gridSize}>
            <FormControl fullWidth size="small">
              <InputLabel>Time Period</InputLabel>
              <Select
                value={timePeriod}
                onChange={(e) => onTimePeriodChange(e.target.value as any)}
                label="Time Period"
              >
                <MenuItem value="week">Week</MenuItem>
                <MenuItem value="month">Month</MenuItem>
                <MenuItem value="quarter">Quarter</MenuItem>
                <MenuItem value="year">Year</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Date Range Filters */}
        {showDateRange && onDateRangeChange && (
          <>
            <Grid item xs={12} sm={6} md={gridSize}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="date"
                value={dateRange?.start || ''}
                onChange={(e) =>
                  onDateRangeChange({
                    start: e.target.value,
                    end: dateRange?.end || '',
                  })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={gridSize}>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="date"
                value={dateRange?.end || ''}
                onChange={(e) =>
                  onDateRangeChange({
                    start: dateRange?.start || '',
                    end: e.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </>
        )}

        {/* Countries Filter */}
        {showCountries && onCountriesChange && (
          <Grid item xs={12} sm={6} md={gridSize}>
            <Autocomplete
              multiple
              size="small"
              options={filterOptions?.countries || []}
              value={countries}
              onChange={(_, newValue) => onCountriesChange(newValue)}
              renderInput={(params) => <TextField {...params} label="Countries" />}
              limitTags={2}
              disabled={!filterOptions}
            />
          </Grid>
        )}

        {/* Cities Filter */}
        {showCities && onCitiesChange && (
          <Grid item xs={12} sm={6} md={gridSize}>
            <Autocomplete
              multiple
              size="small"
              options={
                filterOptions?.cities
                  || (filterOptions?.stores
                    ? Array.from(new Set(
                        filterOptions.stores
                          .map((s: any) => s.city)
                          .filter(Boolean)
                      )).sort() as string[]
                    : [])
              }
              value={cities}
              onChange={(_, newValue) => onCitiesChange(newValue)}
              renderInput={(params) => <TextField {...params} label="Cities" />}
              limitTags={2}
              disabled={!filterOptions}
            />
          </Grid>
        )}

        {/* Retailers Filter */}
        {showRetailers && onRetailersChange && (
          <Grid item xs={12} sm={6} md={gridSize}>
            <Autocomplete
              multiple
              size="small"
              options={filterOptions?.retailers || []}
              value={retailers}
              onChange={(_, newValue) => onRetailersChange(newValue)}
              renderInput={(params) => <TextField {...params} label="Retailers" />}
              limitTags={2}
              disabled={!filterOptions}
            />
          </Grid>
        )}

        {/* Brands Filter */}
        {showBrands && onBrandsChange && (
          <Grid item xs={12} sm={6} md={gridSize}>
            <Autocomplete
              multiple
              size="small"
              options={filterOptions?.brands || []}
              value={brands}
              onChange={(_, newValue) => onBrandsChange(newValue)}
              renderInput={(params) => <TextField {...params} label="Brands" />}
              limitTags={2}
              disabled={!filterOptions}
            />
          </Grid>
        )}

        {/* Stores Filter */}
        {showStores && onStoresChange && (
          <Grid item xs={12} sm={6} md={gridSize}>
            <Autocomplete
              multiple
              size="small"
              options={filterOptions?.stores?.map(s => s.store_name) || []}
              value={stores}
              onChange={(_, newValue) => onStoresChange(newValue)}
              renderInput={(params) => <TextField {...params} label="Stores" />}
              limitTags={2}
              disabled={!filterOptions}
            />
          </Grid>
        )}

        {/* Categories Filter */}
        {showCategories && onCategoriesChange && (
          <Grid item xs={12} sm={6} md={gridSize}>
            <Autocomplete
              multiple
              size="small"
              options={filterOptions?.categories || []}
              value={categories}
              onChange={(_, newValue) => onCategoriesChange(newValue)}
              renderInput={(params) => <TextField {...params} label="Categories" />}
              limitTags={2}
              disabled={!filterOptions}
            />
          </Grid>
        )}

        {/* Subcategories Filter */}
        {showSubcategories && onSubcategoriesChange && (
          <Grid item xs={12} sm={6} md={gridSize}>
            <Autocomplete
              multiple
              size="small"
              options={filterOptions?.subcategories || []}
              value={subcategories}
              onChange={(_, newValue) => onSubcategoriesChange(newValue)}
              renderInput={(params) => <TextField {...params} label="Subcategories" />}
              limitTags={2}
              disabled={!filterOptions}
            />
          </Grid>
        )}

        {/* ABC Classifications Filter */}
        {showAbcClassifications && onAbcClassificationsChange && (
          <Grid item xs={12} sm={6} md={gridSize}>
            <Autocomplete
              multiple
              size="small"
              options={filterOptions?.abc_classifications || ['a', 'b', 'c', 'd', 'e']}
              value={abcClassifications}
              onChange={(_, newValue) => onAbcClassificationsChange(newValue)}
              renderInput={(params) => <TextField {...params} label="ABC Classification" />}
              getOptionLabel={(option) => ABC_LABELS[option.toLowerCase()] || option.toUpperCase()}
              limitTags={2}
            />
          </Grid>
        )}

        {/* Lifecycle Stages Filter */}
        {showLifecycleStages && onLifecycleStagesChange && (
          <Grid item xs={12} sm={6} md={gridSize}>
            <Autocomplete
              multiple
              size="small"
              options={filterOptions?.lifecycle_stages || []}
              value={lifecycleStages}
              onChange={(_, newValue) => onLifecycleStagesChange(newValue)}
              renderInput={(params) => <TextField {...params} label="Lifecycle Stages" />}
              limitTags={2}
              disabled={!filterOptions}
            />
          </Grid>
        )}

        {/* Custom Filters Slot */}
        {customFilters}
      </Grid>

      {/* Action Buttons Row */}
      <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Clear All Button */}
          {activeFilterCount > 0 && (
            <Button
              size="small"
              variant="text"
              onClick={handleClearAll}
            >
              Clear All Filters
            </Button>
          )}

          {/* Save Preset Button */}
          {showPresets && activeFilterCount > 0 && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={handleSavePreset}
            >
              Save Preset
            </Button>
          )}
        </Box>

        {/* Apply Filters Button */}
        {showApplyButton && (
          <Button
            variant="contained"
            size="small"
            onClick={onApplyFilters}
            disabled={isApplying}
            startIcon={isApplying ? <CircularProgress size={16} /> : undefined}
          >
            {isApplying ? 'Applying...' : 'Apply Filters'}
          </Button>
        )}
      </Box>

      {/* Export Menu */}
      <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={() => setExportAnchorEl(null)}
      >
        <MenuItem onClick={() => handleExport('excel')}>
          <ListItemText>Export to Excel</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleExport('pdf')}>
          <ListItemText>Export to PDF</ListItemText>
        </MenuItem>
      </Menu>

      {/* Presets Menu */}
      <Menu
        anchorEl={presetAnchorEl}
        open={Boolean(presetAnchorEl)}
        onClose={() => setPresetAnchorEl(null)}
      >
        {savedPresets.length > 0 ? (
          savedPresets.map((preset: any, index: number) => (
            <MenuItem key={index} onClick={() => handleLoadPreset(index)}>
              <ListItemText>{preset.name}</ListItemText>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <ListItemText>No saved presets</ListItemText>
          </MenuItem>
        )}
        {savedPresets.length > 0 && <Divider />}
        <MenuItem onClick={handleSavePreset} disabled={activeFilterCount === 0}>
          <ListItemIcon>
            <SaveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Save Current Filters</ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default UniversalFilters;
